<template>
  <div class="space-y-4">
    <div>
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Prompts</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Browse and try MCP prompts with arguments
      </p>
    </div>

    <div v-if="store.isLoading" class="flex items-center justify-center py-8">
      <LoadingSpinner size="lg" />
    </div>

    <Alert v-else-if="store.error" variant="error">{{ store.error }}</Alert>

    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else-if="store.prompts.length === 0"
      class="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700"
    >
      <p class="text-sm text-gray-500 dark:text-gray-400">No prompts available</p>
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="prompt in store.prompts"
        :key="prompt.name"
        class="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
      >
        <div class="flex items-start justify-between">
          <div>
            <p class="text-sm font-medium text-gray-900 dark:text-white">{{ prompt.name }}</p>
            <p
              v-if="prompt.description"
              class="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
            >
              {{ prompt.description }}
            </p>
          </div>
          <Button variant="outline" size="sm" @click="tryPrompt(prompt.name)">Try</Button>
        </div>

        <div v-if="prompt.arguments?.length" class="mt-2 space-y-1">
          <div
            v-for="arg in prompt.arguments"
            :key="arg.name"
            class="flex items-center gap-2"
          >
            <label class="text-xs text-gray-600 dark:text-gray-400 w-24 shrink-0">
              {{ arg.name }}{{ arg.required ? ' *' : '' }}
            </label>
            <Input
              :model-value="argValues[prompt.name]?.[arg.name] ?? ''"
              @update:model-value="setArgValue(prompt.name, arg.name, $event as string)"
              :placeholder="arg.description ?? arg.name"
              class="w-full"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Result -->
    <Card v-if="resultMessages.length > 0">
      <div class="flex items-center justify-between mb-2">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">Result</h4>
        <Button variant="ghost" size="sm" @click="resultMessages = []">Close</Button>
      </div>
      <div v-for="(msg, i) in resultMessages" :key="i" class="mb-2">
        <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ msg.role }}:</span>
        <p class="text-sm text-gray-800 dark:text-gray-200 mt-0.5 whitespace-pre-wrap">
          {{ msg.content.text }}
        </p>
      </div>
    </Card>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { Alert, Button, Card, Input, LoadingSpinner } from '@asabdev/asab-ui';
  import { useMcpProtocolStore } from '@/stores/mcpProtocol';
  import type { McpProtocolClient } from '@/lib/mcp/protocolClient';
  import type { McpPromptMessage } from '@/lib/mcp/types';

  interface Props {
    client: McpProtocolClient;
  }

  const props = defineProps<Props>();
  const store = useMcpProtocolStore();

  const argValues = ref<Record<string, Record<string, string>>>({});
  const resultMessages = ref<McpPromptMessage[]>([]);

  const setArgValue = (promptName: string, argName: string, value: string) => {
    if (!argValues.value[promptName]) {
      argValues.value[promptName] = {};
    }
    argValues.value[promptName][argName] = value;
  };

  const tryPrompt = async (name: string) => {
    try {
      const args = argValues.value[name] ?? {};
      const result = await store.getPrompt(props.client, name, args);
      resultMessages.value = result.messages;
    } catch {
      resultMessages.value = [];
    }
  };
</script>
